import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import { Typography, Box } from '@mui/material';
import { mainPageRootStyle } from '../styles';
import { getPost, updatePost } from './actions';
import PostEditForm from './PostEditForm';
import SkeletonForm from '../components/SkeletonForm';
import Breadcrumb from '../components/Breadcrumb';

const PostEditPage = () => {
    const userId = useSelector(state => state.auth.user?.uid);
    const [post, setPost] = useState(null);
    const [images, setImages] = useState([]);
    const [photoSrcs, setPhotoSrcs] = useState([]);
    const [updateImages, setUpdateImages] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { postId } = useParams();
    const location = useLocation();

    const postRedux = useSelector(state => state.scheduler?.post);
    
    // Get handle from URL params or post data
    const queryParams = new URLSearchParams(location.search);
    const handleFromUrl = queryParams.get('handle');

    useEffect(() => {
        document.title = 'Edit Post - Sky Toolbox';
    }, []);

    useEffect(() => {
        dispatch(getPost(postId));
    }, [dispatch, postId]);

    useEffect(() => {
        if (postRedux) {
            setPost({ ...postRedux });
            setPhotoSrcs(postRedux.imageUrls);
        }
    }, [postRedux]);

    // Cleanup: revoke object URLs on unmount to prevent memory leaks
    useEffect(() => {
        return () => {
            photoSrcs.forEach(url => {
                if (url && url.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
        };
    }, [photoSrcs]);

    const handlePostChange = useCallback((p) => {
        setPost(p);
    }, []);

    const handleImagesChange = useCallback(files => {
        setPhotoSrcs(files.map(f => f.fileURL));
        setImages(files.map(f => f.file));
        setUpdateImages(true);
    }, []);

    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        dispatch(updatePost(postId, post, images, userId, updateImages, navigate));
    }, [postId, post, images, userId, updateImages, navigate, dispatch]);

    const handleCancel = useCallback(() => {
        const currentHandle = handleFromUrl || post?.handle;
        if (currentHandle) {
            navigate(`/scheduler?handle=${currentHandle}`);
        } else {
            navigate('/scheduler');
        }
    }, [navigate, post, handleFromUrl]);

    const isLoading = !post;
    // Use handle from URL if available, otherwise from post data
    const handle = handleFromUrl || post?.handle;

    return (
        <Paper style={mainPageRootStyle} sx={{ p: 3 }}>
            <Breadcrumb items={[
                { label: 'Post Scheduler', path: `/scheduler${handle ? `?handle=${handle}` : ''}` },
                { label: 'Edit Scheduled Post' }
            ]} />
            
            {/* Page Header */}
            <Box 
                sx={{ 
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    borderRadius: 3,
                    p: 3,
                    mb: 4,
                    color: 'white',
                }}
            >
                <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 1 }}>
                    ✏️ Edit Scheduled Post
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Update your scheduled post content and timing
                </Typography>
            </Box>
            
            {isLoading ? (
                <SkeletonForm fields={[
                    { height: 60 },
                    { height: 200 },
                    { height: 400 },
                    { height: 40, width: '30%' }
                ]} />
            ) : (
                <PostEditForm
                    handleSubmit={handleSubmit}
                    post={post}
                    handlePostChange={handlePostChange}
                    photoSrcs={photoSrcs}
                    handleImagesChange={handleImagesChange}
                    handleCancel={handleCancel}
                />
            )}
        </Paper>
    );
};

export default PostEditPage;
